// Live channel state for a light (RGB / WWH / WWRGB), shared by both UIs.
//
// Same shape as useRelayStatus: seed from the server's last status, then follow
// `<uno>/client`. Unlike the relays, a light's UI DOES move under the finger —
// dragging a slider or the colour wheel updates locally straight away, because a
// colour picker that only moved when the device replied would be unusable.
// Publishing is what's rate-limited instead.
//
// `applying` drives the "Applying Changes..." veil: Flutter raises it on every
// publish and drops it after a couple of seconds, so a user doesn't tap twice
// while the fixture catches up.

import { useCallback, useEffect, useRef, useState } from 'react';
import {
  subscribeTopic, unsubscribeTopic, addMessageListener,
} from '../mqtt/mqttClient';
import { fetchSeedStatus } from '../devices/deviceStatus';
import { publishCommand } from '../utils/deviceController';
import { formatChannels, parseChannels } from '../devices/light';

export function useLightChannels(uniqueNumber, count, userId, applyMs = 2000) {
  const [values, setValues] = useState(() => new Array(count).fill(0));
  const [applying, setApplying] = useState(false);
  const applyTimer = useRef(null);
  const debounce = useRef(null);

  // Seed from the last recorded status.
  useEffect(() => {
    if (!uniqueNumber) return undefined;
    let alive = true;
    fetchSeedStatus(uniqueNumber).then((raw) => {
      const parsed = parseChannels(raw, count);
      if (alive && parsed) setValues(parsed);
    });
    return () => { alive = false; };
  }, [uniqueNumber, count]);

  // Follow live status.
  useEffect(() => {
    if (!uniqueNumber) return undefined;
    const topic = `${uniqueNumber}/client`;
    subscribeTopic(topic);
    const off = addMessageListener((t, payload) => {
      if (t !== topic) return;
      const open = payload.indexOf('[');
      const close = payload.indexOf(']');
      if (open === -1 || close === -1) return;
      if (payload.substring(open + 1, close) !== String(uniqueNumber)) return;
      const s = `${payload}#`;
      const body = s.substring(s.indexOf(']') + 1, s.indexOf('#'));
      const parsed = parseChannels(body, count);
      if (parsed) setValues(parsed);
    });
    return () => { off(); unsubscribeTopic(topic); };
  }, [uniqueNumber, count]);

  useEffect(() => () => {
    if (applyTimer.current) clearTimeout(applyTimer.current);
    if (debounce.current) clearTimeout(debounce.current);
  }, []);

  const raiseApplying = useCallback(() => {
    setApplying(true);
    if (applyTimer.current) clearTimeout(applyTimer.current);
    applyTimer.current = setTimeout(() => setApplying(false), applyMs);
  }, [applyMs]);

  // Publish the current channel values.
  const commit = useCallback((next) => {
    const payload = formatChannels(next ?? values);
    publishCommand(uniqueNumber, payload, userId);
    raiseApplying();
  }, [values, uniqueNumber, userId, raiseApplying]);

  // Publish a fixed payload (the preset buttons).
  const sendCommand = useCallback((command) => {
    publishCommand(uniqueNumber, command, userId);
    const parsed = parseChannels(command, count);
    if (parsed) setValues(parsed);
    raiseApplying();
  }, [uniqueNumber, userId, count, raiseApplying]);

  // Move the UI now, publish once the user pauses — the colour wheel would
  // otherwise emit a frame per pointer move.
  const setAndDebounce = useCallback((next, ms = 300) => {
    setValues(next);
    if (debounce.current) clearTimeout(debounce.current);
    debounce.current = setTimeout(() => commit(next), ms);
  }, [commit]);

  return {
    values, setValues, applying, commit, sendCommand, setAndDebounce,
  };
}
